/**
 * Сервис для маппинга статусов и ролей между Frontend и Backend
 */
export class MappingService {
    /**
     * Type guard: a string is a recognized FrontendOrderStatus key.
     *
     * @deprecated Wave 1 SoT: use `parseOrderStatus(s) !== null` from
     * `@tenant/shared/contracts` for unknown input, or `isOrderStatus(s)`
     * for the canonical UPPER_CASE form. This alias remains while existing
     * callers migrate; will be removed in Wave 2.
     * See docs/architecture/STATUS_CONTRACT.md.
     */
    static isFrontendOrderStatus(s) {
        return typeof s === 'string' && Object.prototype.hasOwnProperty.call(this.FE_TO_BE_STATUS_MAP, s);
    }
    /**
     * Конвертирует Frontend статус в Backend статус.
     *
     * @deprecated Wave 1 SoT: use `parseOrderStatus` from `@tenant/shared`
     * which accepts both UPPER_CASE and kebab-case and returns `null` on
     * unknown (not throw). This method remains while existing callers
     * migrate; will be removed in Wave 2. See docs/architecture/STATUS_CONTRACT.md.
     */
    static mapStatusToBackend(frontendStatus) {
        const mapped = this.FE_TO_BE_STATUS_MAP[frontendStatus];
        if (!mapped) {
            throw new Error(`MappingService.mapStatusToBackend: unknown frontend order status "${frontendStatus}". ` +
                `Expected one of: ${Object.keys(this.FE_TO_BE_STATUS_MAP).join(', ')}.`);
        }
        return mapped;
    }
    /**
     * Конвертирует Backend заказ в Frontend статус (1:1 mapping)
     */
    static mapStatusToFrontend(order) {
        return this.mapStatusToFrontendSimple(order.status);
    }
    /**
     * Конвертирует Backend статус в Frontend статус (1:1 mapping)
     */
    static mapStatusToFrontendSimple(backendStatus) {
        // 1:1 mapping between backend (UPPER_CASE) and frontend (kebab-case).
        // Wave 3 D1 (2026-05-04): dead V2 enum values (AWAITING_FLORIST,
        // ASSIGNED, ACCEPTED) removed — they were never written, only collapsed.
        const statusMap = {
            'PENDING_PAYMENT': 'pending-payment',
            'PENDING': 'pending',
            'CONFIRMED': 'confirmed',
            'PREPARING': 'preparing',
            'SCHEDULED': 'scheduled',
            'READY': 'ready',
            'IN_TRANSIT': 'in-transit',
            'DELIVERED': 'delivered',
            'CANCELLED': 'cancelled'
        };
        return statusMap[backendStatus] || 'pending';
    }
    /**
     * Конвертирует Frontend роль в Backend роль с флагами
     */
    static mapRoleToBackend(frontendRole) {
        return this.FE_TO_BE_ROLE_MAP[frontendRole];
    }
    static mapFloristConfirmationStatusToBackend(frontendStatus) {
        return this.FE_TO_BE_FLORIST_CONFIRMATION_MAP[frontendStatus];
    }
    static mapFloristConfirmationStatusToFrontend(backendStatus) {
        const statusMap = {
            PENDING: 'pending',
            ACCEPTED: 'accepted',
            COMPLETED: 'completed',
            DECLINED: 'declined'
        };
        return statusMap[backendStatus];
    }
    static mapChatStatusToBackend(frontendStatus) {
        return this.FE_TO_BE_CHAT_STATUS_MAP[frontendStatus];
    }
    static mapChatStatusToFrontend(backendStatus) {
        const statusMap = {
            NEW: 'new',
            OPEN: 'open',
            PENDING: 'pending',
            RESOLVED: 'resolved',
            CLOSED: 'closed'
        };
        return statusMap[backendStatus];
    }
    /**
     * Конвертирует Backend пользователя в Frontend роль.
     *
     * Wave 2 PR-W2.3: signature simplified to `Pick<BackendUser, 'role'>`.
     * The legacy `ADMIN+isFloristManager → dispatcher` fallback is gone
     * along with the dispatcher role itself; the flag columns will be
     * dropped in the same migration. Legacy JWTs carrying role=DISPATCHER
     * or SUPPORT are normalized via parseUserRole at the auth boundary
     * (see middleware/auth.ts) — this method itself fails closed on
     * unknown roles.
     */
    static mapRoleToFrontend(user) {
        const roleMap = {
            'ADMIN': 'admin',
            'FLORIST': 'florist',
            'COURIER': 'courier',
            'CUSTOMER': 'customer',
        };
        const mapped = roleMap[user.role];
        if (!mapped) {
            throw new Error(`Unknown backend role: ${user.role}`);
        }
        return mapped;
    }
    /**
     * Получает список доступных переходов статусов для роли
     */
    static getAvailableStatusTransitions(currentStatus, userRole) {
        // Wave 2 PR-W2.3: collapsed to 5-role canonical (admin/florist/courier/
        // customer/guest). dispatcher rows folded into admin (admin had ≥ super
        // set always). manager-florist / delivery-manager rows dropped (legacy
        // aliases removed). support row dropped — support did not perform
        // transitions in any cell anyway. florist now also handles READY →
        // DELIVERED for in-store pickup (gated server-side by FSM, see
        // shared/services/orderWorkflow.ts).
        const transitions = {
            'pending-payment': {
                admin: ['pending', 'cancelled'],
                customer: ['cancelled'],
            },
            'pending': {
                admin: ['confirmed', 'cancelled'],
                customer: ['cancelled'],
            },
            'confirmed': {
                admin: ['pending', 'preparing', 'cancelled'],
                florist: ['preparing', 'cancelled'],
            },
            'preparing': {
                admin: ['ready', 'cancelled'],
                florist: ['ready', 'cancelled'],
            },
            'ready': {
                admin: ['in-transit', 'delivered', 'cancelled'],
                florist: ['delivered', 'cancelled'],
                courier: ['in-transit', 'cancelled'],
            },
            'in-transit': {
                admin: ['delivered', 'cancelled'],
                courier: ['delivered', 'cancelled'],
            },
            'delivered': {},
            'cancelled': {},
            'scheduled': {
                admin: ['pending', 'cancelled'],
                customer: ['cancelled'],
            },
        };
        return transitions[currentStatus]?.[userRole] || [];
    }
    /**
     * Проверяет, может ли пользователь с данной ролью изменить статус
     */
    static canChangeStatus(fromStatus, toStatus, userRole) {
        const availableTransitions = this.getAvailableStatusTransitions(fromStatus, userRole);
        return availableTransitions.includes(toStatus);
    }
}
// Маппинг Frontend статусов в Backend (1:1 mapping)
MappingService.FE_TO_BE_STATUS_MAP = {
    'pending-payment': 'PENDING_PAYMENT',
    'pending': 'PENDING',
    'confirmed': 'CONFIRMED',
    'preparing': 'PREPARING',
    'ready': 'READY',
    'in-transit': 'IN_TRANSIT',
    'delivered': 'DELIVERED',
    'cancelled': 'CANCELLED',
    'scheduled': 'SCHEDULED'
};
// Маппинг Frontend ролей в Backend.
// Wave 2 PR-W2.3: dispatcher/support/manager-florist/delivery-manager
// removed. The flag-based fallback (ADMIN+isFloristManager → dispatcher)
// also removed — see mapRoleToFrontend below.
MappingService.FE_TO_BE_ROLE_MAP = {
    'admin': { role: 'ADMIN' },
    'florist': { role: 'FLORIST' },
    'courier': { role: 'COURIER' },
    'customer': { role: 'CUSTOMER' },
};
MappingService.FE_TO_BE_FLORIST_CONFIRMATION_MAP = {
    pending: 'PENDING',
    accepted: 'ACCEPTED',
    completed: 'COMPLETED',
    declined: 'DECLINED'
};
MappingService.FE_TO_BE_CHAT_STATUS_MAP = {
    new: 'NEW',
    open: 'OPEN',
    pending: 'PENDING',
    resolved: 'RESOLVED',
    closed: 'CLOSED'
};
